import React from "react";
import "./FleetServices.css";

/**
 * Fleet Services grid (white background version)
 * - Full-width car service image below title
 * - 3×3 grid layout for services (no images)
 * - Clean design with title and description only
 *
 * Usage:
 *   <section className="page">
 *     <FleetServices />
 *   </section>
 */
export default function FleetServices({
  phoneNumber = "253-319-2612",
}) {
  const items = [
    { title: "Oil Changes", desc: "Quick lube with OEM-spec oil & filters to protect your engine and extend service intervals." },
    { title: "Diagnostics", desc: "Computerized scanning and guided testing to find the root cause fast—no guesswork." },
    { title: "Steering & Suspension", desc: "Shocks, struts, tie-rods, ball joints, and alignments to restore control and ride comfort." },
    { title: "Brakes", desc: "Pads, rotors, calipers, and hydraulics—complete brake inspections and repairs for safe stops." },
    { title: "DOT Inspections (10,500LB or higher)", desc: "Comprehensive DOT safety inspections to keep commercial vehicles compliant and road-ready." },
    { title: "Prepurchase Inspections", desc: "Thorough condition reports before you buy so you avoid costly hidden problems." },
    { title: "Used Car Safety Inspections", desc: "Multi-point checks and road tests to verify the vehicle's everyday safety and reliability." },
    { title: "Timing Belts", desc: "Scheduled replacements with tensioners, idlers, and seals as required to prevent engine damage." },
    { title: "Timing Chains", desc: "Noise, stretch, and phase issues corrected with quality chain kits and precise timing procedures." },
    { title: "Air Conditioning Diagnostics & Repair", desc: "Leak detection, compressor and component repairs, and recharges to keep cabin temps comfortable." },
    { title: "Transmissions", desc: "Fluid services, diagnostics, and repairs—from slipping and harsh shifts to leaks and sensors." },
    { title: "Engines", desc: "Minor to major engine repairs, gasket and seal work, cooling system service, and performance concerns." },
    { title: "Mobile Services", desc: "We come to your home, office, or job site—convenient on-site maintenance and repairs." },
    { title: "Drop Off Services", desc: "Prefer a shop visit? Drop off at our partner bays for the same professional, scheduled service." },
    { title: "Tune Ups", desc: "Spark plugs, coils, filters, and throttle/intake cleaning to restore performance and economy." },
  ];

  // Build a safe tel: URL (strip non-digits, add +1 for US)
  const digits = String(phoneNumber).replace(/\D+/g, "");
  const telHref =
    digits.startsWith("1") && digits.length === 11
      ? `tel:+${digits}`
      : `tel:+1${digits}`;

  return (
    <section className="fs-wrap" aria-labelledby="fs-title">
      <div className="fs-header">
        <p className="fs-kicker">FLEET MAINTENANCE SERVICES</p>
        <h2 id="fs-title" className="fs-title">
          We offer a wide range
          <br /> of fleet services
        </h2>
      </div>

      {/* Full-width car service image */}
      <div className="fs-hero-image">
        <img
          src="./herobg.jpeg"
          alt="Professional car service and maintenance"
          loading="lazy"
        />
        <div className="fs-hero-overlay" />
      </div>

      {/* 3×3 Services Grid */}
      <div className="fs-grid">
        {items.map((it, i) => (
          <article className="fs-grid-card" key={i}>
            <div className="fs-grid-content">
              <h3 className="fs-grid-title">{it.title}</h3>
              <p className="fs-grid-desc">{it.desc}</p>
            </div>
            <div className="fs-grid-hover" />
          </article>
        ))}
      </div>

      {/* CTA Section */}
      <div className="fs-cta-row">
        <a
          className="fs-cta"
          href={telHref}
          aria-label={`Call Dempsey Mobile Service at ${phoneNumber}`}
          role="button"
        >
          <span className="fs-cta-text">Call us</span>
          <span className="fs-cta-icon">▸</span>
        </a>
      </div>
    </section>
  );
}